/**
 * MorphingDashboard
 * Context-Aware Dashboard that transforms based on:
 * - Bio State (stress level, energy)
 * - Location (office, court, home)
 * - Matter Stage (closing, litigation, etc.)
 */

import { Suspense, lazy, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUIPreferences, useMatterState, useBioState } from '../../context/LawityContext';
import type { SuggestedLayout } from '../../context/LawityContext';

// Lazy load layout components
const FocusLayout = lazy(() => import('./layouts/FocusLayout'));
const KanbanLayout = lazy(() => import('./layouts/KanbanLayout'));
const TimelineLayout = lazy(() => import('./layouts/TimelineLayout'));
const AnalyticsLayout = lazy(() => import('./layouts/AnalyticsLayout'));
const DefaultLayout = lazy(() => import('./layouts/DefaultLayout'));
const CalendarLayout = lazy(() => import('./layouts/CalendarLayout'));
const ClientLayout = lazy(() => import('./layouts/ClientLayout'));
const BillingLayout = lazy(() => import('./layouts/BillingLayout'));

// Loading skeleton
function LayoutSkeleton() {
  return (
    <div className="animate-pulse space-y-4 p-6">
      <div className="h-8 bg-neutral-200 dark:bg-neutral-800 rounded w-1/4" />
      <div className="grid grid-cols-3 gap-4">
        <div className="h-40 bg-neutral-200 dark:bg-neutral-800 rounded" />
        <div className="h-40 bg-neutral-200 dark:bg-neutral-800 rounded" />
        <div className="h-40 bg-neutral-200 dark:bg-neutral-800 rounded" />
      </div>
      <div className="h-64 bg-neutral-200 dark:bg-neutral-800 rounded" />
    </div>
  );
}

// Layout renderer
interface LayoutRendererProps {
  layout: SuggestedLayout;
}

function LayoutRenderer({ layout }: LayoutRendererProps) {
  const Component = useMemo(() => {
    switch (layout) {
      case 'focus':
        return FocusLayout;
      case 'kanban':
        return KanbanLayout;
      case 'timeline':
        return TimelineLayout;
      case 'analytics':
        return AnalyticsLayout;
      case 'calendar':
        return CalendarLayout;
      case 'clients':
        return ClientLayout;
      case 'billing':
        return BillingLayout;
      default:
        return DefaultLayout;
    }
  }, [layout]);

  return (
    <Suspense fallback={<LayoutSkeleton />}>
      <Component />
    </Suspense>
  );
}

// Context indicator showing why this layout was chosen
// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface ContextIndicatorProps {
  layout: SuggestedLayout;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ContextIndicator({ layout: _layout }: ContextIndicatorProps) {
  const { bio } = useBioState();
  const { matter } = useMatterState();

  const reason = useMemo(() => {
    if (bio.stress === 'high' || bio.stress === 'critical') {
      return {
        icon: '🧘',
        text: 'Focus Mode: High stress detected',
        color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20',
      };
    }
    if (matter.stage === 'closing') {
      return {
        icon: '📋',
        text: `Kanban: ${matter.name} is closing`,
        color: 'text-green-600 bg-green-50 dark:bg-green-900/20',
      };
    }
    if (matter.stage === 'litigation' || matter.stage === 'discovery') {
      return {
        icon: '⏱️',
        text: `Timeline: ${matter.name} in ${matter.stage}`,
        color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20',
      };
    }
    return null;
  }, [bio.stress, matter.stage, matter.name]);

  if (!reason) return null;

  return (
    <motion.div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${reason.color}`}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <span>{reason.icon}</span>
      <span>{reason.text}</span>
    </motion.div>
  );
}

/**
 * Main MorphingDashboard Component
 */
export function MorphingDashboard() {
  const { suggestedLayout, simplifiedMode } = useUIPreferences();

  return (
    <div className={`min-h-screen ${simplifiedMode ? 'simplified-mode' : ''}`}>
      {/* Context Indicator */}
      <div className="px-6 py-3">
        <ContextIndicator layout={suggestedLayout} />
      </div>

      {/* Layout Container */}
      <AnimatePresence mode="wait">
        <motion.div
          key={suggestedLayout}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <LayoutRenderer layout={suggestedLayout} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default MorphingDashboard;
